import React, { useEffect } from 'react'
import { IonHeader, IonPage, IonTitle, IonToolbar, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { setCurrentStep, setCurrentSubStep } from '../store/pulsara.actions'
import PulsaraContent from '../content/Pulsara.content'
import Stepper from '../../../components/stepper/Stepper.component'
import { PATIENT_MATCH_SUB_STEP_IDS, STEP_IDS, PULSARA_STEPS } from '../constants'
import { getPulsaraCurrentStep, getPulsaraCurrentSubStep, getSelectedDateRange } from '../selectors'
import LabeledSelect from '../../../components/labeledSelect/LabeledSelect.component'
import PatientInfo from './patientInfo/PatientInfo.component'
import PulsaraSubStepper from './PulsaraSubStepper.component'
import { getPulsaraOptionsSelector } from '../../options/selectors'
import { IDateRangeOption, IOption } from '../../../common/common.types'
import { AuthenticationService } from '../../login/services/authentication.service'
import { getUserSelector } from '../../user/selectors'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { useHistory } from 'react-router'
import Logger from '../../../services/logger.service'
import PulsaraFooter from './footer/PulsaraFooter.component'
import { fetchPulsaraRecords } from '../store/pulsara.actions'

const Pulsara: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentStep = useSelector(getPulsaraCurrentStep)
  const currentSubStep = useSelector(getPulsaraCurrentSubStep)
  const selectedDateRange = useSelector(getSelectedDateRange) as IOption | null
  const pulsaraOptions = useSelector(getPulsaraOptionsSelector)
  const { pulsaraDateRangeOptions } = pulsaraOptions
  const user = useSelector(getUserSelector)
  const history = useHistory()

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)

  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn(t('common.userNotAuthenticatedWarning'))
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes, history, t])

  useIonViewWillEnter(() => {
    if (!pulsaraDateRangeOptions?.length) return
    const defaultDateRange = pulsaraDateRangeOptions[0] || null
    dispatch(fetchPulsaraRecords(defaultDateRange))
  }, [pulsaraDateRangeOptions])

  useIonViewDidLeave(() => {
    dispatch(setCurrentStep(STEP_IDS.PULSARA_SEARCH))
    dispatch(setCurrentSubStep(1))
  })

  const onDateRangeChange = (option: IDateRangeOption) => {
    if (selectedDateRange?.id === option.id) return
    dispatch(fetchPulsaraRecords(option))
  }

  const showDateRangeSelector = () => {
    return currentStep === STEP_IDS.PULSARA_SEARCH && currentSubStep === PATIENT_MATCH_SUB_STEP_IDS.PATIENT_SEARCH_FORM
  }

  const renderHeader = () => {
    return (
      <>
        <PCRHeader />
        <IonHeader className='ion-no-padding'>
          <IonToolbar color='light'>
            <IonTitle>{t('pulsara.PulsaraImport')}</IonTitle>
          </IonToolbar>
          <IonToolbar color='light' className='ion-no-padding'>
            <Stepper steps={PULSARA_STEPS} currentStep={currentStep} />
            {currentStep !== STEP_IDS.PULSARA_SEARCH && <PatientInfo />}
            {showDateRangeSelector() && (
              <LabeledSelect
                prefixLabel={t('pulsara.DateRange')}
                label={t('pulsara.DateRange')}
                options={pulsaraDateRangeOptions}
                selectedOption={selectedDateRange}
                onOptionChange={(option: IOption) => onDateRangeChange(option as IDateRangeOption)}
                listHeader={t('pulsara.SelectDateRange')}
                dataTestID='PulsaraDateRange'
                hideClearButton
              />
            )}
            {currentStep === STEP_IDS.IMPORT && <PulsaraSubStepper />}
          </IonToolbar>
        </IonHeader>
      </>
    )
  }

  return (
    <IonPage id='PulsaraPage'>
      {renderHeader()}
      <PulsaraContent />
      <PulsaraFooter />
    </IonPage>
  )
}

export default Pulsara
